import { CloseCircleFilled } from "@ant-design/icons";
import { Button, InputNumber } from "antd";
import { InputNumberProps } from "antd";
import classNames from "classnames";
import { FieldWrapper } from "components/UI/Table/UI/InteractiveCell/components/FieldWrapper.tsx";
import { WithSaveButton } from "components/UI/Table/UI/InteractiveCell/components/WithSaveButton.tsx";
import {
    useInteractiveCell,
    useSafeFormContext,
} from "components/UI/Table/UI/InteractiveCell/hooks/useInteractiveCell.ts";
import { AlwaysEditingFieldProps, FieldProps } from "components/UI/Table/UI/InteractiveCell/miscs/types.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useEffect, useState } from "react";
import { FieldValues, Path } from "react-hook-form";

type Props<T extends FieldValues> = InputNumberProps &
    FieldProps<T> & {
        clearable?: boolean;
    } & AlwaysEditingFieldProps;

export const WithInputNumber = <T extends FieldValues = FieldValues>({ formControl, ...props }: Props<T>) => {
    const { onSave, onChange, value, className, clearable = false, alwaysEditing = false, ...restProps } = props;
    const { t } = usePaldockTranslation("tables.ui.interactive-cell");

    const {
        setControlledValue,
        handleOnSaveSideEffects,
        resolvedValue,
        isFormControlled,
        handleFocus,
        handleBlur,
        fieldRef,
        error,
    } = useInteractiveCell({
        formControl,
        value,
    });

    const formContext = useSafeFormContext<T>();
    const [draftValue, setDraftValue] = useState<number | null>(null);

    useEffect(() => {
        setDraftValue(resolvedValue as number | null);
    }, [resolvedValue]);

    const handleChange: InputNumberProps["onChange"] = (val) => {
        setDraftValue(val as number | null);
        if (alwaysEditing && isFormControlled && formContext && formControl?.name) {
            formContext.setValue(formControl.name as Path<T>, val as never, {
                shouldValidate: true,
                shouldDirty: true,
            });
        }
        onChange?.(val);
    };

    const handleClear = () => {
        setDraftValue(null);
        if (alwaysEditing && isFormControlled && formContext && formControl?.name) {
            formContext.setValue(formControl.name as Path<T>, null as never, {
                shouldValidate: true,
                shouldDirty: true,
            });
        }
        onChange?.(null);
    };

    const handleSave = () => {
        if (isFormControlled && formContext && formControl?.name) {
            formContext.setValue(formControl.name as Path<T>, draftValue as never, {
                shouldValidate: true,
                shouldDirty: true,
            });
        } else {
            setControlledValue(draftValue);
        }
        handleOnSaveSideEffects();
        onSave?.(draftValue);
    };

    return (
        <FieldWrapper
            displayValue={resolvedValue}
            ref={fieldRef}
            isError={!!error}
            errorMessage={error?.message as string}
            overlay={props.overlay}
            alwaysEditing={alwaysEditing}
        >
            <WithSaveButton onSave={handleSave} hideSaveButton={alwaysEditing}>
                <InputNumber
                    {...restProps}
                    className={classNames(className, "!w-full")}
                    value={draftValue as InputNumberProps["value"]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                />
                {clearable && draftValue !== null && (
                    <Button
                        type="text"
                        size="small"
                        icon={<CloseCircleFilled />}
                        aria-label={t("actions.clear")}
                        data-row-click-ignore="true"
                        className="!cursor-pointer text-neutral-400 hover:!text-neutral-600"
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                            event.stopPropagation();
                            handleClear();
                        }}
                    />
                )}
            </WithSaveButton>
        </FieldWrapper>
    );
};
